import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../firebase/firebaseConfig';
import { auth, db } from '../../firebase/firebaseConfig';
import { fetchUserPremiumAccess } from '../../utils/subscriptionEntitlement';

// OpenAI API configuration - for direct API fallback
const OPENAI_API_ENDPOINT = 'https://api.openai.com/v1/chat/completions';
// Note: Direct API fallback should only be used in development with a valid API key
// In production, all requests should go through Firebase Functions
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY || '';

/** Must exceed Cloud Function `processAIRequest` timeout + round-trip (see functions/aiService.ts). */
const AI_CALLABLE_TIMEOUT_MS = 150_000;

// Cache for storing previous AI responses with LRU (Least Recently Used) functionality
class LRUCache {
  constructor(maxSize = 100) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.accessOrder = [];
  }

  has(key) {
    return this.cache.has(key);
  }

  get(key) {
    if (!this.cache.has(key)) return undefined;

    // Update access order (move to end = most recently used)
    this.updateAccessOrder(key);

    return this.cache.get(key);
  }

  set(key, value) {
    // If key already exists, update access order
    if (this.cache.has(key)) {
      this.updateAccessOrder(key);
    } else {
      // Add to access order
      this.accessOrder.push(key);

      // If cache is full, remove least recently used item
      if (this.accessOrder.length > this.maxSize) {
        const lruKey = this.accessOrder.shift();
        this.cache.delete(lruKey);
      }
    }

    // Set the value
    this.cache.set(key, value);
    return this;
  }

  delete(key) {
    if (!this.cache.has(key)) return false;

    // Remove from access order
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }

    // Remove from cache
    return this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
    this.accessOrder = [];
  }

  updateAccessOrder(key) {
    // Remove from current position
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }

    // Add to end (most recently used)
    this.accessOrder.push(key);
  }
}

// Create a global LRU cache instance
const responseCache = new LRUCache(100);

/**
 * AIWrapper - A backend component that handles AI-related functionality
 * 
 * This wrapper provides methods for:
 * 1. Getting AI explanations for a single line of code
 * 2. Getting AI explanations for an entire code snippet
 * 3. Generating code snippets from user prompts
 * 
 * Optimized for performance with:
 * - Response caching
 * - Request batching
 * - Efficient error handling
 * - Memory optimization
 */
class AIWrapper {
  constructor() {
    this.model = 'gpt-4';
    this.cache = responseCache;
    this.pendingRequests = new Map();
    this.abortControllers = new Map();
  }

  /**
   * Generates a cache key from messages
   * 
   * @param {Array} messages - Array of message objects
   * @returns {string} - Cache key
   */
  generateCacheKey(messages) {
    return JSON.stringify(messages);
  }

  /**
   * Makes a request to the AI service with caching and rate limiting
   * 
   * @param {Array} messages - Array of message objects to send to the API
   * @param {boolean} isPremium - Whether the user has premium status
   * @returns {Promise} - Promise that resolves to the API response
   */
  async makeRequest(messages, isPremium = false) {
    // Generate cache key
    const cacheKey = this.generateCacheKey(messages);

    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Check if there's already a pending request for this exact query
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create an AbortController for this request
    const controller = new AbortController();
    this.abortControllers.set(cacheKey, controller);

    // Create the request promise
    const requestPromise = (async () => {
      try {
        // Check if user is logged in
        if (!auth.currentUser) {
          throw new Error('You must be logged in to use AI features.');
        }

        // Get premium status directly from Firestore if not provided
        let userIsPremium = isPremium;

        if (!userIsPremium) {
          try {
            const userId = auth.currentUser.uid;
            const access = await fetchUserPremiumAccess(db, userId);
            userIsPremium = access.isPremium;
          } catch (error) {
            console.error('Error checking subscription status directly:', error);
          }
        }

        // Call the Firebase cloud function
        const functions = getFunctions(app);
        const processAIRequest = httpsCallable(functions, 'processAIRequest');

        // console.log('Calling processAIRequest with params:', {
        //   isPremium: userIsPremium,
        //   model: this.model,
        //   messagesCount: messages.length
        // });

        let callableTimeoutId;
        const timeoutPromise = new Promise((_, reject) => {
          callableTimeoutId = setTimeout(
            () => reject(new Error('Request timed out')),
            AI_CALLABLE_TIMEOUT_MS
          );
        });

        let result;
        try {
          result = await Promise.race([
            processAIRequest({
              isPremium: userIsPremium,
              messages,
              model: this.model
            }),
            timeoutPromise
          ]);
        } finally {
          if (callableTimeoutId !== undefined) {
            clearTimeout(callableTimeoutId);
          }
        }

        // Check for errors
        if (result.data.error) {
          const errorCode = result.data.code || 'UNKNOWN_ERROR';
          const errorMessage = result.data.message || 'Error processing AI request';
          throw new Error(errorMessage);
        }

        // Handle both old and new response formats
        const content = result.data.content;

        // Store in cache
        this.cache.set(cacheKey, content);

        // Log remaining limits and usage if available
        if (result.data.limits) {
          console.log('AI Usage Limits:', result.data.limits);
        }

        if (result.data.usage) {
          console.log('AI Usage Stats:', result.data.usage);
        }

        return content;
      } catch (error) {
        // Don't cache errors
        if (error.name !== 'AbortError') {
          console.error('AI API request failed:', error);

          // Add more detailed error logging
          if (error.code) {
            console.error('Error code:', error.code);
          }
          if (error.details) {
            console.error('Error details:', error.details);
          }

          // Create a more informative error message
          let errorMessage = error.message || 'Unknown error occurred';

          // Check for quota exceeded error in the message
          if (error.message && error.message.includes('quota') && error.message.includes('exceeded')) {
            console.log('OpenAI quota exceeded, using fallback response');
            // Instead of throwing an error, return a fallback response
            const fallbackResponse = this.generateFallbackResponse(messages);
            this.cache.set(cacheKey, fallbackResponse);
            return fallbackResponse;
          } else if (error.code === 'internal') {
            errorMessage = 'Internal server error. This might be due to an issue with the OpenAI API key or service configuration. Please try again later or contact support.';
          } else if (error.code === 'unavailable') {
            errorMessage = 'Service unavailable. The AI service is currently not responding. Please try again later.';
          } else if (error.code === 'resource-exhausted') {
            errorMessage = 'Resource limits exceeded. You may have reached your usage quota. Please try again later or upgrade your plan.';
          }

          const enhancedError = new Error(errorMessage);
          enhancedError.originalError = error;
          throw enhancedError;
        }
        throw error;
      } finally {
        // Clean up
        this.pendingRequests.delete(cacheKey);
        this.abortControllers.delete(cacheKey);
      }
    })();

    // Store the pending request
    this.pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }

  /**
   * Handles Firebase function errors by attempting direct API fallback
   * 
   * @param {string} errorCode - The error code from Firebase
   * @param {string} errorMessage - The error message
   * @param {Array} messages - The original messages array
   * @param {string} cacheKey - The cache key for this request
   * @returns {Promise<string>} - Promise that resolves to the API response or fallback
   */
  async handleFirebaseFunctionError(errorCode, errorMessage, messages, cacheKey) {
    console.error(`Handling Firebase function error: ${errorCode} - ${errorMessage}`);
    console.error('Automatically attempting direct API fallback with hardcoded API key');

    try {
      // Try direct API request without user confirmation since we have the API key hardcoded
      try {
        const directResponse = await this.makeDirectApiRequest(messages);
        // console.log('Direct API request successful');

        // Store in cache
        this.cache.set(cacheKey, directResponse);

        return directResponse;
      } catch (directApiError) {
        console.error('Direct API request failed:', directApiError);
        console.error(`Direct API error details: ${directApiError.message}`);
        // Fall back to generic response
      }
    } catch (fallbackError) {
      console.error('Error in direct API fallback flow:', fallbackError);
    }

    // If direct API fails, use generic fallback response
    // console.log('Using generic fallback response');
    return this.generateFallbackResponse(messages);
  }

  /**
   * Cancels all pending requests
   */
  cancelAllRequests() {
    for (const controller of this.abortControllers.values()) {
      controller.abort();
    }
    this.pendingRequests.clear();
    this.abortControllers.clear();
  }

  /**
   * Explains a single line of code
   * 
   * @param {string} line - The line of code to explain
   * @param {string} language - The programming language
   * @param {string} context - Optional additional context about the code
   * @param {boolean} isPremium - Whether the user has premium status
   * @returns {Promise<string>} - Promise that resolves to the explanation
   */
  async explainLineOfCode(line, language, context = '', isPremium = false) {
    // Trim inputs to ensure consistent caching
    const trimmedLine = line.trim();
    const trimmedLanguage = language.trim().toLowerCase();
    const trimmedContext = context.trim();

    const messages = [
      {
        role: 'system',
        content: `You are an expert programming tutor specializing in ${trimmedLanguage}. 
                 Explain the following line of code in a clear, concise manner. 
                 Focus on what the code does, any patterns or idioms used, and potential edge cases.
                 Keep your explanation under 150 words.`
      },
      {
        role: 'user',
        content: `Please explain this line of ${trimmedLanguage} code: \`${trimmedLine}\`
                 ${trimmedContext ? `\nAdditional context: ${trimmedContext}` : ''}`
      }
    ];

    return this.makeRequest(messages, isPremium);
  }

  /**
   * Explains an entire code snippet
   * 
   * @param {string} snippet - The code snippet to explain
   * @param {string} language - The programming language
   * @param {string} title - Optional title or description of the snippet
   * @param {boolean} isPremium - Whether the user has premium status
   * @returns {Promise<string>} - Promise that resolves to the explanation
   */
  async explainCodeSnippet(snippet, language, title = '', isPremium = false) {
    // Trim inputs to ensure consistent caching
    const trimmedSnippet = snippet.trim();
    const trimmedLanguage = language.trim().toLowerCase();
    const trimmedTitle = title.trim();

    const messages = [
      {
        role: 'system',
        content: `You are an expert programming tutor specializing in ${trimmedLanguage}.
                 Provide a comprehensive explanation of the following code snippet.
                 Break down the explanation into sections:
                 1. Overview - What the code does at a high level
                 2. Key Components - The main parts/functions and their purpose
                 3. Logic Flow - How the code executes step by step
                 4. Best Practices - What's done well or could be improved
                 Use markdown formatting for clarity.`
      },
      {
        role: 'user',
        content: `Please explain this ${trimmedLanguage} code snippet${trimmedTitle ? ` titled "${trimmedTitle}"` : ''}:
                 \`\`\`${trimmedLanguage}
                 ${trimmedSnippet}
                 \`\`\``
      }
    ];

    return this.makeRequest(messages, isPremium);
  }

  /**
   * Generates metadata (title, description, tags) for a code snippet
   * 
   * @param {string} code - The code snippet to generate metadata for
   * @param {string} language - The programming language of the code
   * @param {string} prompt - Optional original prompt that generated the code
   * @returns {Promise<Object>} - Promise that resolves to an object with title, description, and tags
   */
  async generateSnippetDetails(code, language, prompt = '') {
    // Trim inputs to ensure consistent caching
    const trimmedCode = code.trim();
    const trimmedLanguage = language.trim().toLowerCase();
    const trimmedPrompt = prompt.trim();

    const promptContext = trimmedPrompt
      ? `\nThe code was generated based on this prompt: "${trimmedPrompt}"`
      : '';

    const messages = [
      {
        role: 'system',
        content: `You are an expert ${trimmedLanguage} developer.
                 Analyze the provided code snippet and generate appropriate metadata for it.
                 Format your response as a JSON object with three fields:
                 1. "title": A concise, descriptive title for the snippet (max 50 chars)
                 2. "description": A clear explanation of what the code does (max 200 chars)
                 3. "tags": An array of 3-5 relevant tags/keywords related to the code`
      },
      {
        role: 'user',
        content: `Generate metadata for this ${trimmedLanguage} code snippet:
                 \`\`\`${trimmedLanguage}
                 ${trimmedCode}
                 \`\`\`${promptContext}`
      }
    ];

    const response = await this.makeRequest(messages);

    try {
      // Parse the JSON response
      const parsedResponse = JSON.parse(response);
      return parsedResponse;
    } catch (error) {
      console.warn('Failed to parse JSON response for snippet details, using fallback extraction');

      // Fallback extraction using regex
      const titleMatch = response.match(/title["\s:]+([^"]+)/i);
      const descriptionMatch = response.match(/description["\s:]+([^"]+)/i);
      const tagsMatch = response.match(/tags["\s:]+\[(.*?)\]/is);

      const title = titleMatch ? titleMatch[1].trim() : 'Code Snippet';
      const description = descriptionMatch ? descriptionMatch[1].trim() : 'A useful code snippet';

      let tags = [];
      if (tagsMatch) {
        // Extract tags from the array format
        const tagsString = tagsMatch[1];
        tags = tagsString.split(',')
          .map(tag => tag.trim().replace(/"/g, ''))
          .filter(tag => tag.length > 0);
      } else {
        // Default tags based on language
        tags = [trimmedLanguage];
      }

      return {
        title,
        description,
        tags
      };
    }
  }

  /**
   * Generates only code based on a user prompt without explanation
   * 
   * @param {string} prompt - The user's description of what they want
   * @param {string} language - The programming language to use
   * @param {Array} constraints - Optional array of constraints or requirements
   * @returns {Promise<string>} - Promise that resolves to the generated code as a string
   */
  async generateCodeOnly(prompt, language, constraints = []) {
    // Trim inputs to ensure consistent caching
    const trimmedPrompt = prompt.trim();
    const trimmedLanguage = language.trim().toLowerCase();
    const trimmedConstraints = constraints.map(c => c.trim());

    const constraintsText = trimmedConstraints.length > 0
      ? `\nAdditional requirements:\n${trimmedConstraints.map(c => `- ${c}`).join('\n')}`
      : '';

    const messages = [
      {
        role: 'system',
        content: `You are an expert ${trimmedLanguage} developer. 
                 Generate ONLY high-quality, well-documented code based on the user's requirements.
                 Your code should follow best practices, be efficient, and handle edge cases.
                 Include comments to explain complex parts within the code.
                 
                 IMPORTANT: Your response should ONLY contain the code itself, with no explanations, introductions, or conclusions outside of code comments.
                 Do not wrap the code in markdown code blocks or any other formatting - just return the raw code.
                 
                 Example of a good response:
                 function calculateSum(a, b) {
                   // Add two numbers and return the result
                   return a + b;
                 }
                 
                 Example of a bad response:
                 Here's a function to calculate the sum of two numbers:
                 
                 \`\`\`
                 function calculateSum(a, b) {
                   return a + b;
                 }
                 \`\`\`
                 
                 This function takes two parameters and returns their sum.`
      },
      {
        role: 'user',
        content: `Create a ${trimmedLanguage} code snippet that does the following:
                 ${trimmedPrompt}${constraintsText}
                 
                 Remember to return ONLY the code, no explanations or markdown formatting.`
      }
    ];

    const response = await this.makeRequest(messages);

    // Clean up the response to ensure it's just code
    // Remove any markdown code blocks if they exist
    let cleanedCode = response.replace(/^```[\w]*\n|```$/gm, '').trim();

    return cleanedCode;
  }

  /**
   * Generates only an explanation for a code snippet or concept
   * 
   * @param {string} prompt - The user's description of what they want explained
   * @param {string} code - The code to explain (optional)
   * @param {string} language - The programming language
   * @returns {Promise<string>} - Promise that resolves to the explanation as a string
   */
  async generateExplanationOnly(prompt, code = '', language) {
    // Trim inputs to ensure consistent caching
    const trimmedPrompt = prompt.trim();
    const trimmedCode = code.trim();
    const trimmedLanguage = language.trim().toLowerCase();

    const messages = [
      {
        role: 'system',
        content: `You are an expert ${trimmedLanguage} developer and educator.
                 Provide a clear, concise explanation of the code or concept described by the user.
                 Your explanation should be informative and educational, covering:
                 - What the code/concept does at a high level
                 - How it works
                 - Any important design patterns or principles used
                 - Potential edge cases or limitations
                 
                 Format your response as plain text with no code blocks or special formatting.`
      },
      {
        role: 'user',
        content: trimmedCode
          ? `Please explain this ${trimmedLanguage} code:
             
             ${trimmedCode}
             
             Additional context: ${trimmedPrompt}`
          : `Please explain the following concept in ${trimmedLanguage}:
             
             ${trimmedPrompt}`
      }
    ];

    const response = await this.makeRequest(messages);
    return response.trim();
  }

  /**
   * Generates a code snippet based on a user prompt
   * 
   * @param {string} prompt - The user's description of what they want
   * @param {string} language - The programming language to use
   * @param {Array} constraints - Optional array of constraints or requirements
   * @returns {Promise<Object>} - Promise that resolves to an object with code and explanation
   */
  async generateCodeSnippet(prompt, language, constraints = []) {
    // Trim inputs to ensure consistent caching
    const trimmedPrompt = prompt.trim();
    const trimmedLanguage = language.trim().toLowerCase();
    const trimmedConstraints = constraints.map(c => c.trim());

    const constraintsText = trimmedConstraints.length > 0
      ? `\nAdditional requirements:\n${trimmedConstraints.map(c => `- ${c}`).join('\n')}`
      : '';

    const messages = [
      {
        role: 'system',
        content: `You are an expert ${trimmedLanguage} developer. 
                 Generate high-quality, well-documented code based on the user's requirements.
                 Your code should follow best practices, be efficient, and handle edge cases.
                 Include comments ONLY to explain complex parts within the code.
                 
                 IMPORTANT: Do NOT include explanations within the code itself. Keep the code clean and production-ready.
                 
                 After the code, provide a separate brief explanation of how it works and any design decisions you made.
                 
                 Format your response as a JSON object with two fields:
                 1. "code": The generated code as a string (ONLY the actual code, no explanations outside of normal code comments)
                 2. "explanation": Your explanation as a string (all explanatory text goes here, not in the code)`
      },
      {
        role: 'user',
        content: `Create a ${trimmedLanguage} code snippet that does the following:
                 ${trimmedPrompt}${constraintsText}`
      }
    ];

    const response = await this.makeRequest(messages);

    try {
      // Parse the JSON response
      const parsedResponse = JSON.parse(response);
      return parsedResponse;
    } catch (error) {
      // If parsing fails, try to extract code and explanation using regex
      console.warn('Failed to parse JSON response, using fallback extraction');

      // Fallback extraction
      const codeMatch = response.match(/```(?:\w+)?\s*([\s\S]*?)```/);
      const code = codeMatch ? codeMatch[1].trim() : '';

      // Everything after the last code block is considered the explanation
      const explanation = response.split('```').pop().trim();

      return {
        code,
        explanation: explanation || 'No explanation provided.'
      };
    }
  }

  /**
   * Makes a direct request to the OpenAI API
   * This is used as a fallback when the Firebase function fails
   * 
   * @param {Array} messages - Array of message objects to send to the API
   * @returns {Promise<string>} - Promise that resolves to the API response content
   */
  async makeDirectApiRequest(messages) {
    // console.log('Attempting direct API request to OpenAI as fallback...');

    try {
      // Use the API key from environment variable (only for development)
      const apiKey = OPENAI_API_KEY;

      // If no API key is available, throw an error instead of making a request
      if (!apiKey || apiKey.trim() === '') {
        throw new Error('OpenAI API key is not configured. Please use Firebase Functions for AI requests.');
      }

      // console.log('Using API key for direct API request');

      // Make the request to the OpenAI API with improved error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), AI_CALLABLE_TIMEOUT_MS);

      try {
        const response = await fetch(OPENAI_API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey.trim()}`
          },
          body: JSON.stringify({
            model: this.model,
            messages: messages,
            temperature: 0.7,
            max_tokens: 2048
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Check if the request was successful
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Direct OpenAI API error:', errorData);

          // Create more specific error messages based on status code
          let errorMessage = `OpenAI API error: ${response.status} ${response.statusText}`;

          if (response.status === 401) {
            errorMessage = 'Authentication error: Invalid API key or token';
          } else if (response.status === 429) {
            errorMessage = 'Rate limit exceeded: Too many requests to the OpenAI API';
          } else if (response.status === 500) {
            errorMessage = 'OpenAI server error: The AI service is experiencing issues';
          } else if (errorData.error && errorData.error.message) {
            errorMessage = `OpenAI API error: ${errorData.error.message}`;
          }

          throw new Error(errorMessage);
        }

        // Parse the response
        const data = await response.json();
        // console.log('Direct API response:', data);

        if (!data.choices || !data.choices[0] || !data.choices[0].message) {
          console.error('Invalid OpenAI API response structure:', data);
          throw new Error('Invalid response from OpenAI API');
        }

        const content = data.choices[0].message.content;
        // console.log('Direct API response content:', content);

        // Return the content
        return content;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('Direct API request failed:', error);

      // Provide more helpful error messages
      if (error.name === 'AbortError') {
        throw new Error('Direct API request timed out after 60 seconds');
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Unable to connect to OpenAI API. Please check your internet connection.');
      }

      throw error;
    }
  }

  /**
   * Generates a fallback response when the Firebase function fails
   * 
   * @param {Array} messages - The original messages sent to the API
   * @returns {string} - A fallback response
   */
  generateFallbackResponse(messages) {
    // console.log('Generating generic fallback response for messages:', messages);

    // Extract the user's query from the messages
    let userQuery = '';
    for (const message of messages) {
      if (message.role === 'user') {
        userQuery = message.content;
        break;
      }
    }

    // Determine the type of request based on the system message
    let requestType = 'unknown';
    let language = 'code';

    for (const message of messages) {
      if (message.role === 'system') {
        const content = message.content.toLowerCase();
        if (content.includes('explain')) {
          requestType = 'explanation';
        } else if (content.includes('generate')) {
          requestType = 'generation';
        }

        // Try to extract the language
        const languageMatch = content.match(/specializing in (\w+)|expert (\w+) developer/i);
        if (languageMatch) {
          language = (languageMatch[1] || languageMatch[2]).trim();
        }

        break;
      }
    }

    // Generate an appropriate fallback response
    let fallbackResponse = '';

    if (requestType === 'explanation') {
      fallbackResponse = `I apologize, but I'm currently unable to provide a detailed explanation due to a temporary service disruption. 

Here are some general tips for understanding ${language} code:

1. Break down the code into smaller parts and understand each part's function.
2. Look for common patterns and idioms in the language.
3. Check the documentation for any built-in functions or libraries being used.
4. Consider the inputs, processing, and outputs of the code.
5. Try running the code with different inputs to see how it behaves.

Please try your request again later when our service has been restored.`;
    } else if (requestType === 'generation') {
      fallbackResponse = `I apologize, but I'm currently unable to generate code due to a temporary service disruption. 

Here are some suggestions for writing ${language} code:

1. Start by outlining the requirements and breaking them down into smaller tasks.
2. Use clear variable and function names that describe their purpose.
3. Follow ${language} best practices and coding conventions.
4. Include comments to explain complex logic.
5. Test your code with different inputs to ensure it works correctly.

Please try your request again later when our service has been restored.`;
    } else {
      fallbackResponse = `I apologize, but I'm currently unable to process your request due to a temporary service disruption. Please try again later when our service has been restored.`;
    }

    return fallbackResponse;
  }

  /**
   * Clears the response cache
   * 
   * @param {string} [pattern] - Optional regex pattern to selectively clear cache entries
   */
  clearCache(pattern) {
    // Just clear the entire cache for now to avoid any issues
    this.cache.clear();
  }
}

/**
 * React hook for using the AIWrapper
 * 
 * @returns {Object} - Object containing the AIWrapper instance and state variables
 */
export const useAI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create a stable reference to the AIWrapper instance
  const aiWrapperRef = useRef(null);

  // Initialize the AIWrapper instance only once
  if (!aiWrapperRef.current) {
    aiWrapperRef.current = new AIWrapper();
  }

  // Clean up function to cancel pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (aiWrapperRef.current) {
        aiWrapperRef.current.cancelAllRequests();
      }
    };
  }, []);

  /**
   * Wrapper for explainLineOfCode that handles loading and error states
   */
  const explainLine = useCallback(async (line, language, context = '', isPremium = false) => {
    if (!line || !language) {
      return Promise.reject(new Error('Line and language are required'));
    }

    setLoading(true);
    setError(null);
    try {
      const explanation = await aiWrapperRef.current.explainLineOfCode(line, language, context, isPremium);
      setLoading(false);
      return explanation;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
      setLoading(false);
      throw err;
    }
  }, []);

  /**
   * Wrapper for explainCodeSnippet that handles loading and error states
   */
  const explainSnippet = useCallback(async (snippet, language, title = '', isPremium = false) => {
    if (!snippet || !language) {
      return Promise.reject(new Error('Snippet and language are required'));
    }

    setLoading(true);
    setError(null);
    try {
      const explanation = await aiWrapperRef.current.explainCodeSnippet(snippet, language, title, isPremium);
      setLoading(false);
      return explanation;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
      setLoading(false);
      throw err;
    }
  }, []);

  /**
   * Wrapper for generateCodeSnippet that handles loading and error states
   */
  const generateCode = useCallback(async (prompt, language, constraints = []) => {
    if (!prompt || !language) {
      return Promise.reject(new Error('Prompt and language are required'));
    }

    setLoading(true);
    setError(null);
    try {
      const result = await aiWrapperRef.current.generateCodeSnippet(prompt, language, constraints);
      setLoading(false);
      return result;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
      setLoading(false);
      throw err;
    }
  }, []);

  /**
   * Wrapper for generateSnippetDetails that handles loading and error states
   */
  const generateDetails = useCallback(async (code, language, prompt = '') => {
    if (!code || !language) {
      return Promise.reject(new Error('Code and language are required'));
    }

    setLoading(true);
    setError(null);
    try {
      const result = await aiWrapperRef.current.generateSnippetDetails(code, language, prompt);
      setLoading(false);
      return result;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
      setLoading(false);
      throw err;
    }
  }, []);

  /**
   * Clears the response cache
   * 
   * @param {string} [pattern] - Optional regex pattern to selectively clear cache entries
   */
  const clearCache = useCallback((pattern) => {
    if (aiWrapperRef.current) {
      aiWrapperRef.current.clearCache(pattern);
    }
  }, []);

  /**
   * Cancels all pending requests
   */
  const cancelRequests = useCallback(() => {
    if (aiWrapperRef.current) {
      aiWrapperRef.current.cancelAllRequests();
      setLoading(false);
    }
  }, []);

  /**
   * Wrapper for generateCodeOnly that handles loading and error states
   */
  const generateCodeOnly = useCallback(async (prompt, language, constraints = []) => {
    if (!prompt || !language) {
      return Promise.reject(new Error('Prompt and language are required'));
    }

    setLoading(true);
    setError(null);
    try {
      const code = await aiWrapperRef.current.generateCodeOnly(prompt, language, constraints);
      setLoading(false);
      return code;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
      setLoading(false);
      throw err;
    }
  }, []);

  /**
   * Wrapper for generateExplanationOnly that handles loading and error states
   */
  const generateExplanationOnly = useCallback(async (prompt, code = '', language) => {
    if (!language) {
      return Promise.reject(new Error('Language is required'));
    }

    setLoading(true);
    setError(null);
    try {
      const explanation = await aiWrapperRef.current.generateExplanationOnly(prompt, code, language);
      setLoading(false);
      return explanation;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
      setLoading(false);
      throw err;
    }
  }, []);

  return {
    explainLine,
    explainSnippet,
    generateCode,
    generateCodeOnly,
    generateExplanationOnly,
    generateDetails,
    clearCache,
    cancelRequests,
    loading,
    error
  };
};

export default AIWrapper;
